import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { DataContext } from './DataContext';
import { _onRenderNavLink } from './ZedXMainMenu';
import { App03Frameworks } from '../types/App03Workflow';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { TruncatedText } from './TruncatedText';
import { IEntityDescription1 } from '../types';
import { decodeRouteId } from '../utils/routeId';

interface ICorporateWorkflowsMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    frameworkID: App03Frameworks;
    entityDescriptionForMenu: IEntityDescription1;
    onWorkItemsFilterChange?: (filter: string) => void;
    selectedWorkItemsFilter?: string | null;
    backlogStagesCount: number;
}

type ChildView = 'tasks' | 'epics' | 'workflow-users' | 'workstreams' | 'work-item-types' | 'ask-cloe' | 'none';

export const CorporateWorkflowsMenu = ({
    frameworkID,
    entityDescriptionForMenu,
    onWorkItemsFilterChange,
    selectedWorkItemsFilter,
    backlogStagesCount,
    errorHandling: { clearErrors },
    onLinkClicked
}: ICorporateWorkflowsMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();
    const { dataServices } = useContext(DataContext);
    const [lastParentId, setLastParentId] = useState<number | null>(null);

    const [navs, setNavs] = useState<INavLink[]>([]);
    const resetErrors = useCallback(clearErrors, [clearErrors]);

    const [totalPortfoliosCount, setTotalPortfoliosCount] = useState(0);
    const [totalWorkflowsCount, setTotalWorkflowsCount] = useState(0);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const hasInitialisedFromRoute = useRef(false);

    const countTotalPortfolios = () => {
        dataServices.app03PortfolioService
            .countAllForList(frameworkID)
            .then(p => setTotalPortfoliosCount(p.length ?? 0))
            .catch(() => setTotalPortfoliosCount(0));
    };

    const countTotalWorkflows = () => {
        dataServices.app03WorkflowService
            .countAllForList(frameworkID)
            .then(wfs => setTotalWorkflowsCount(wfs.length ?? 0))
            .catch(() => setTotalWorkflowsCount(0));
    };

    useEffect(() => {
        countTotalPortfolios();
        countTotalWorkflows();
    }, [frameworkID, dataServices]);

    const handleFilterClick = (filterKey: string) => {
        onWorkItemsFilterChange?.(filterKey);
        if (onLinkClicked) onLinkClicked();
    };

    // ------------------------------------------------
    // Parse contextual menu mode (same pattern as Agile)
    // ------------------------------------------------
    const parseMenuMode = (path: string): {
        isContextualMenu: boolean;
        parentType?: string;
        parentId?: number;
        childView?: ChildView;
    } => {

        const portfolioRegex = /^\/a1\/workflows-for-portfolio\/([^/]+)/;
        const matchPortfolio = path.match(portfolioRegex);
        if (matchPortfolio) {
            return {
                isContextualMenu: true,
                parentType: 'portfolio',
                parentId: decodeRouteId(matchPortfolio[1]),
                childView: 'workstreams'
            };
        }

        const workflowTasksRegex = /^\/a1\/workflow-tasks\/([^/]+)/;
        const matchWorkflowTasks = path.match(workflowTasksRegex);
        if (matchWorkflowTasks) {
            return {
                isContextualMenu: true,
                parentType: 'workstream',
                parentId: decodeRouteId(matchWorkflowTasks[1]),
                childView: 'tasks'
            };
        }

        const epicsRegex = /^\/a1\/epics\/([^/]+)/;
        const matchEpics = path.match(epicsRegex);
        if (matchEpics) {
            return {
                isContextualMenu: true,
                parentType: 'workstream',
                parentId: decodeRouteId(matchEpics[1]),
                childView: 'epics'
            };
        }

        const usersRegex = /^\/a1\/workflow-users\/([^/]+)/;
        const matchUsers = path.match(usersRegex);
        if (matchUsers) {
            return {
                isContextualMenu: true,
                parentType: 'workstream',
                parentId: decodeRouteId(matchUsers[1]),
                childView: 'workflow-users'
            };
        }

        const workItemTypesRegex = /^\/a1\/settings\/work-item-types\/([^/]+)/;
        const matchWorkItemTypes = path.match(workItemTypesRegex);
        if (matchWorkItemTypes) {
            return {
                isContextualMenu: true,
                parentType: 'template',
                parentId: decodeRouteId(matchWorkItemTypes[1]),
                childView: 'work-item-types'
            };
        }

        const askCloeRegex = /^\/a1\/ask-cloe\/([^/]+)/;
        if (askCloeRegex.test(path)) {
            return { isContextualMenu: true, childView: 'ask-cloe' };
        }

        return { isContextualMenu: false, childView: 'none' };
    };

    const { isContextualMenu, parentType, parentId, childView } = parseMenuMode(pathname);

    // -----------------------------------------
    // Reset filter behavior:
    // - Only reset when entering/leaving workflow-tasks child (tasks view), or when not contextual
    // - Don't reset when changing between childViews that are NOT tasks (e.g., tasks -> epics) unless you want that
    // -----------------------------------------
    useEffect(() => {
        // If we're in a workstream 'tasks' view and the parentId changed, reset filter to Current Stages
        if (isContextualMenu && parentType === "workstream" && childView === 'tasks' && parentId != null) {
            if (parentId !== lastParentId) {
                // Reset filter to Current Stages
                console.log('resetting filter to current stages');
                onWorkItemsFilterChange?.('Current Stages');
                setLastParentId(parentId);
            }
            return;
        }

        // If we left contextual mode entirely, reset to Current Stages as before
        if (!isContextualMenu) {
            onWorkItemsFilterChange?.('Current Stages');
            setLastParentId(null);
            return;
        }

        // If we switched to a workstream child that is NOT tasks (e.g., epics), we *do not* force-reset the tasks filter.
        // If you want a different behavior (e.g., clear filter when going to epics), handle it here.
    }, [parentId, lastParentId, isContextualMenu, parentType, childView, onWorkItemsFilterChange]);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route (GLOBAL MENU)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current || isContextualMenu) return;

        if (
            pathname.startsWith('/a1/settings/templates')
        ) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname, isContextualMenu]);

    // ------------------------------------------------
    // Build navigation
    // ------------------------------------------------
    useEffect(() => {

        const routerNav = (path: string): void => {
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }: { name: string; path: string }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        let links: INavLink[] = [];

        // -------- TASKS (FILTER MODE) --------
        if (isContextualMenu && parentType === 'workstream' && childView === 'tasks') {
            links = [
                NamePathToNavLink({ name: 'Home', path: '/' }),
                NamePathToNavLink({ name: 'Welcome', path: '/a1/welcome' }),
                {
                    key: 'work-items-parent',
                    name: 'Work Items',
                    url: null,
                    isExpanded: true,
                    links: [
                        ...(backlogStagesCount > 0
                            ? [{ key: 'Backlog', name: 'Backlog', url: '', onClick: () => handleFilterClick('Backlog') }]
                            : []),
                        { key: 'Current Stages', name: 'Current Stages', url: '', onClick: () => handleFilterClick('Current Stages') },
                        { key: 'Epic', name: 'Epic', url: '', onClick: () => handleFilterClick('Epic') },
                        { key: 'Archived', name: 'Archived', url: '', onClick: () => handleFilterClick('Archived') }
                    ]
                }
            ];

            setNavs(links);
            return;
        }

        // -------- ASK CLOE (CONTEXTUAL) --------
        if (isContextualMenu && childView === 'ask-cloe') {
            links = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a1/welcome' },
                { name: 'Cloe Chat', path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        // -------- OTHER CONTEXTUAL MODES --------
        if (isContextualMenu && parentType === 'workstream') {
            links = [
                NamePathToNavLink({ name: 'Home', path: '/' }),
                NamePathToNavLink({ name: 'Welcome', path: '/a1/welcome' }),
                NamePathToNavLink({ name: childView === 'epics' ? 'Epics' : 'Workflow Users', path: pathname })
            ];

            setNavs(links);
            return;
        }

        if (isContextualMenu && parentType === 'portfolio') {
            links = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a1/welcome' },
                { name: 'Workstreams', path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        // -------- TEMPLATE → WORK ITEM TYPES (CONTEXTUAL) --------
        if (isContextualMenu && parentType === 'template' && childView === 'work-item-types') {
            links = [
                NamePathToNavLink({ name: 'Home', path: '/' }),
                NamePathToNavLink({ name: 'Welcome', path: '/a1/welcome' }),
                NamePathToNavLink({
                    name: 'Work Item Types',
                    path: pathname
                })
            ];

            setNavs(links);
            return;
        }


        // -------- GLOBAL MENU --------
        links = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a1/welcome' },
            { name: 'Insights', path: '/a1/insights' },
            { name: 'Ask Cloe', path: '/a1/ai-chats' },
            { name: 'Portfolios', path: '/a1/cwf-portfolios' },
            { name: 'Workstreams', path: '/a1/workflows' }
        ].map(NamePathToNavLink);

        if (up.UserIsSystemAdmin()) {
            links.push({
                key: 'gw_settings',
                name: 'Settings',
                url: null,
                isExpanded: settingsExpanded,
                onClick: () => setSettingsExpanded(prev => !prev),
                links: [
                    { name: 'Templates', path: '/a1/settings/templates' },
                ].map(NamePathToNavLink)
            });
        }

        setNavs(links);

    }, [up, history, onLinkClicked, resetErrors, pathname, isContextualMenu, parentType, childView]);

    const selectedKey =
        isContextualMenu && parentType === 'workstream' && childView === 'tasks'
            ? selectedWorkItemsFilter ?? undefined
            : pathname;

    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div style={{
                    width: '100%',
                    paddingLeft: '15px',
                    paddingBottom: '1px',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-GroupWorkStreams.png`}
                        alt="Corporate Workflows"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                <LoggedUserInfoForMenu />

                {isContextualMenu && entityDescriptionForMenu && (
                    <div style={{
                        backgroundColor: '#1A619F',
                        color: 'white',
                        padding: '10px 15px',
                        margin: '5px 10px 10px',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                <Nav
                    selectedKey={selectedKey}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(
                            lnk,
                            totalWorkflowsCount,
                            totalPortfoliosCount
                        )
                    }
                />
            </div>
        </ThemeProvider>
    );
};